/**
 * Zustand Store for Player Data
 */

import { create } from 'zustand';
import type { MissingPlayer, PlayerFilters } from '@/types';

interface PlayerStore {
  // State
  players: MissingPlayer[];
  filters: PlayerFilters;
  isLoading: boolean;
  error: string | null;

  // Actions
  setPlayers: (players: MissingPlayer[]) => void;
  setFilters: (filters: Partial<PlayerFilters>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed
  getFilteredPlayers: () => MissingPlayer[];
}

export const usePlayerStore = create<PlayerStore>((set, get) => ({
  // Initial state
  players: [],
  filters: {
    position: 'all',
    team: 'all',
    search: '',
  },
  isLoading: false,
  error: null,

  // Actions
  setPlayers: (players) => set({ players }),

  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  // Computed - get filtered players
  getFilteredPlayers: () => {
    const { players, filters } = get();

    return players.filter((player) => {
      // Position filter
      if (filters.position !== 'all' && player.position !== filters.position) {
        return false;
      }

      // Team filter
      if (filters.team !== 'all' && player.team_2025 !== filters.team) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName =
          player.name.toLowerCase().includes(searchLower) ||
          player.name_en.toLowerCase().includes(searchLower);
        if (!matchesName) {
          return false;
        }
      }

      return true;
    });
  },
}));
